<template>
  <div v-if="count > 0" class="floating-outlet-cart">
    <button
      type="button"
      class="floating-outlet-cart__trigger"
      aria-label="Ver carro de packs"
      @click="toggleDrawer"
    >
      <span class="floating-outlet-cart__icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="21" r="1"/>
          <circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
      </span>
      <span class="floating-outlet-cart__badge">{{ count }}</span>
    </button>
    <Transition name="cart-drawer">
      <div v-show="open" class="floating-outlet-cart__drawer">
        <div class="floating-outlet-cart__drawer-header">
          <h4 class="floating-outlet-cart__drawer-title">Tu carro</h4>
          <button type="button" class="floating-outlet-cart__drawer-close" aria-label="Cerrar" @click="open = false">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="floating-outlet-cart__drawer-body">
          <p v-if="displayCart.length === 0" class="floating-outlet-cart__empty">
            No hay packs en el carro.
          </p>
          <template v-else>
          <div
            v-for="(pack, index) in displayCart"
            :key="pack.id || `pack-${index}`"
            class="floating-outlet-cart__item"
          >
            <div class="floating-outlet-cart__item-header">
              <strong class="floating-outlet-cart__item-title">{{ pack.title || 'Pack' }}</strong>
              <span class="floating-outlet-cart__item-location">{{ formatLocation(pack.Location) || (pack.LocationId ? `Sede #${pack.LocationId}` : '') }}</span>
            </div>
            <ul class="floating-outlet-cart__item-courses">
              <li
                v-for="(detail, dIdx) in (pack.PackDetails || [])"
                :key="detail.courseId || detail.Course?.id || `c-${index}-${dIdx}`"
                class="floating-outlet-cart__item-course"
              >
                {{ cleanCourseName(detail.Course?.name) || `Curso ${dIdx + 1}` }}
              </li>
            </ul>
            <div class="floating-outlet-cart__item-footer">
              <span class="floating-outlet-cart__item-price">{{ (pack.priceOfert != null ? pack.priceOfert : pack.price) | moneyFormatter }}</span>
              <button
                v-if="showRemove"
                type="button"
                class="floating-outlet-cart__item-remove"
                aria-label="Quitar del carro"
                @click="remove(pack.id)"
              >
                Quitar
              </button>
            </div>
          </div>
          </template>
        </div>
        <div class="floating-outlet-cart__drawer-footer">
          <template v-if="discountPercent > 0">
            <div class="floating-outlet-cart__total">
              <span class="floating-outlet-cart__total-label">Subtotal</span>
              <strong class="floating-outlet-cart__total-value floating-outlet-cart__total-value--strikethrough">{{ cartTotal | moneyFormatter }}</strong>
            </div>
            <div class="floating-outlet-cart__total">
              <span class="floating-outlet-cart__total-label">% de descuento por sesiones</span>
              <strong class="floating-outlet-cart__total-value">{{ discountPercent }}%</strong>
            </div>
          </template>
          <div class="floating-outlet-cart__total">
            <span class="floating-outlet-cart__total-label">Total</span>
            <strong class="floating-outlet-cart__total-value">{{ discountedTotal | moneyFormatter }}</strong>
          </div>
          <button type="button" class="floating-outlet-cart__btn-checkout" @click="goToCheckout">
            Continuar
          </button>
          <a v-if="showBackToPacks" href="#" class="floating-outlet-cart__link-back" @click.prevent="goToPacks">
            Seguir agregando packs
          </a>
        </div>
      </div>
    </Transition>
    <div v-if="open" class="floating-outlet-cart__backdrop" @click="open = false" aria-hidden="true"/>
  </div>
</template>

<script>
import outletCartService from '@/services/outletCartService';

export default {
  name: 'FloatingOutletCart',
  props: {
    cartCount: { type: Number, default: null },
    cartItems: { type: Array, default: null },
    showRemove: { type: Boolean, default: true },
    showBackToPacks: { type: Boolean, default: true },
  },
  data() {
    return {
      open: false,
      cart: [],
      discountPercent: 0,
    };
  },
  computed: {
    displayCart() {
      if (this.cart && Array.isArray(this.cart) && this.cart.length > 0) {
        return this.cart;
      }
      if (this.cartItems && Array.isArray(this.cartItems) && this.cartItems.length > 0) {
        return this.cartItems;
      }
      return [];
    },
    count() {
      if (this.cartCount != null && this.cartCount >= 0) return this.cartCount;
      return this.displayCart.length;
    },
    cartTotal() {
      return this.displayCart.reduce((sum, pack) => {
        const price = parseInt(pack.priceOfert, 10) || parseInt(pack.price, 10) || 0;
        return sum + price;
      }, 0);
    },
    totalSessions() {
      return this.displayCart.reduce((sum, pack) => {
        const details = Array.isArray(pack.PackDetails) ? pack.PackDetails : [];
        const sessions = details.reduce((detailSum, d) => detailSum + (parseInt(d.sessions, 10) || 0), 0);
        return sum + sessions;
      }, 0);
    },
    discountedTotal() {
      const discount = Number(this.discountPercent) || 0;
      if (discount <= 0) return this.cartTotal;
      return Math.round(this.cartTotal * (1 - discount / 100));
    },
  },
  created() {
    this.refresh();
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.refresh();
      }
    },
  },
  methods: {
    toggleDrawer() {
      if (!this.open) {
        this.refresh();
        this.$nextTick(() => {
          this.open = true;
          this.fetchDiscount();
        });
      } else {
        this.open = false;
      }
    },
    refresh() {
      const items = outletCartService.getCart();
      this.cart = Array.isArray(items) ? [...items] : [];
    },
    async fetchDiscount() {
      const quantity = this.totalSessions;
      if (quantity <= 0) {
        this.discountPercent = 0;
        return;
      }
      try {
        const { data } = await this.Axios.get('/dashadmin/packs/discount-by-sessions', {
          params: { quantity }
        });
        this.discountPercent = Number(data?.discount) || 0;
      } catch (error) {
        console.error('Discount fetch error:', error);
        this.discountPercent = 0;
      }
    },
    formatLocation(location) {
      if (!location || !location.name) return '';
      return location.name
        .replace(/^Preuniversitario\s*/, 'Sede ')
        .replace(/^Pre\s*/, 'Sede ');
    },
    cleanCourseName(name) {
      if (!name) return '';
      let out = name
        .replace(/\s\d{3}$/, '')
        .replace(/\b(Tercero|Tarde|Egresado)\b/g, '')
        .trim();
      if (out === 'Lenguaje') return 'Competencia Lectora';
      if (out === 'Matemática') return 'Competencia Matemática M1';
      if (out === 'Matemática Electivo') return 'Competencia Matemática M2';
      return out;
    },
    remove(packId) {
      outletCartService.removePack(packId);
      this.refresh();
      this.$nextTick(() => {
        this.fetchDiscount();
        this.$emit('cart-updated', this.cart);
      });
    },
    goToCheckout() {
      this.open = false;
      this.$emit('checkout');
      const first = this.cart[0];
      if (first) {
        this.$router.push({ name: 'Outlet2', query: { packId: first.id, fromCart: '1' } });
      } else {
        this.$router.push({ name: 'Outlet2', query: { fromCart: '1' } });
      }
    },
    goToPacks() {
      this.open = false;
      this.$router.push({ name: 'Outlet1' });
    },
  },
};
</script>

<style lang="scss" scoped>
.floating-outlet-cart {
  position: fixed;
  top: 13px;
  right: 24px;
  z-index: 10001;
  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
  }
}

.floating-outlet-cart__trigger {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #3691d8;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover {
    transform: scale(1.05);
  }
  &:focus {
    outline: 2px solid #1b1b1d;
    outline-offset: 2px;
  }
}

.floating-outlet-cart__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-outlet-cart__badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #1b1b1d;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
}

.floating-outlet-cart__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: -1;
}

.floating-outlet-cart__drawer {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: 70vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  z-index: 1;
  overflow: hidden;
  @media (max-width: 768px) {
    width: 320px;
    max-height: 60vh;
  }
}

.floating-outlet-cart__drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.floating-outlet-cart__drawer-title {
  margin: 0;
  font-family: 'Poppins-Bold', sans-serif;
  font-size: 18px;
  color: #1b1b1d;
}

.floating-outlet-cart__drawer-close {
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  &:hover {
    background: #f3f4f6;
    color: #1b1b1d;
  }
}

.floating-outlet-cart__drawer-body {
  overflow-y: auto;
  padding: 12px;
  flex: 1;
  min-height: 0;
}

.floating-outlet-cart__empty {
  margin: 0;
  padding: 12px 0;
  font-size: 14px;
  color: #6b7280;
  font-family: 'Poppins', sans-serif;
}

.floating-outlet-cart__item {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #fafafa;
  &:last-child {
    margin-bottom: 0;
  }
}

.floating-outlet-cart__item-header {
  margin-bottom: 8px;
}

.floating-outlet-cart__item-title {
  display: block;
  font-family: 'Poppins-Medium', sans-serif;
  font-size: 14px;
  color: #1b1b1d;
  line-height: 1.3;
}

.floating-outlet-cart__item-location {
  font-size: 12px;
  color: #6b7280;
  font-family: 'Poppins', sans-serif;
}

.floating-outlet-cart__item-courses {
  margin: 0 0 8px 0;
  padding-left: 16px;
  font-size: 12px;
  color: #4e4f55;
  line-height: 1.5;
}

.floating-outlet-cart__item-course {
  margin-bottom: 2px;
}

.floating-outlet-cart__item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.floating-outlet-cart__item-price {
  font-family: 'Poppins-Bold', sans-serif;
  font-size: 14px;
  color: #1b1b1d;
}

.floating-outlet-cart__item-remove {
  font-size: 12px;
  color: #3691d8;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-family: 'Poppins', sans-serif;
  &:hover {
    color: #1b1b1d;
  }
}

.floating-outlet-cart__drawer-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.floating-outlet-cart__total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}
.floating-outlet-cart__total-label {
  font-family: 'Poppins-Medium', sans-serif;
  font-size: 16px;
  color: #1b1b1d;
}
.floating-outlet-cart__total-value {
  font-family: 'Poppins-Bold', sans-serif;
  font-size: 18px;
  color: #1b1b1d;

  &--strikethrough {
    text-decoration: line-through;
  }
}

.floating-outlet-cart__btn-checkout {
  width: 100%;
  padding: 12px 24px;
  border-radius: 100px;
  background: #3691d8;
  color: #fff;
  border: none;
  font-family: 'Poppins-Medium', sans-serif;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #2a7bc7;
  }
  &:focus {
    outline: 2px solid #1b1b1d;
    outline-offset: 2px;
  }
}

.floating-outlet-cart__link-back {
  text-align: center;
  font-size: 14px;
  color: #3691d8;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  &:hover {
    text-decoration: underline;
  }
}

.cart-drawer-enter-active,
.cart-drawer-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.cart-drawer-enter-from,
.cart-drawer-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
